export type TranscriptInput = {
	name: string
	kind: 'query' | 'code' | 'memory'
	lang?: string
	value: string
}

export type TranscriptTool = {
	name: string
	summary: string
	note: string
	inputs: Array<TranscriptInput>
	resultLang?: string
	result: string
}

export type TranscriptFile = {
	path: string
	summary: string
	lang?: string
	content: string
}

export type TranscriptLine =
	| { role: 'user'; text: string }
	| { role: 'agent'; text: string; tone?: 'reasoning' }
	| { role: 'email'; subject: string; text: string }
	| { role: 'tools'; tools: Array<TranscriptTool> }
	| {
			role: 'files'
			summary: string
			note: string
			files: Array<TranscriptFile>
	  }

export type TranscriptScene = 'phone'

export type TranscriptAct = {
	id: string
	kicker: string
	title: string
	/** Homepage loop only: start a new surface after this act header. */
	scene?: TranscriptScene
	/** Homepage loop divider before this act. Defaults to “Later”. */
	later?: string
	lines: Array<TranscriptLine>
}

export function transcriptFileLang(path: string, lang?: string) {
	if (lang) return lang
	if (path.endsWith('.ts')) return 'ts'
	if (path.endsWith('.json')) return 'json'
	if (path.endsWith('.md')) return 'md'
	return 'txt'
}

export function collectTranscriptSnippets(
	acts: Array<TranscriptAct>,
): Array<{ code: string; lang: string }> {
	const snippets: Array<{ code: string; lang: string }> = []
	for (const act of acts) {
		for (const line of act.lines) {
			switch (line.role) {
				case 'user':
				case 'agent':
				case 'email':
					break
				case 'tools':
					for (const tool of line.tools) {
						for (const input of tool.inputs) {
							snippets.push({
								code: input.value,
								lang: input.lang ?? 'json',
							})
						}
						snippets.push({
							code: tool.result,
							lang: tool.resultLang ?? 'json',
						})
					}
					break
				case 'files':
					for (const file of line.files) {
						snippets.push({
							code: file.content,
							lang: transcriptFileLang(file.path, file.lang),
						})
					}
					break
				default: {
					const exhaustive: never = line
					throw new Error(
						`Unhandled transcript line: ${JSON.stringify(exhaustive)}`,
					)
				}
			}
		}
	}
	return snippets
}

export function jsonInput(value: unknown) {
	return JSON.stringify(value, null, 2)
}

export function memoryContextInput(context: {
	task: string
	entities: Array<string>
}) {
	return {
		name: 'memoryContext',
		kind: 'memory' as const,
		lang: 'json',
		value: jsonInput(context),
	}
}

export function conversationIdInput(conversationId: string) {
	return {
		name: 'conversationId',
		kind: 'query' as const,
		lang: 'json',
		value: jsonInput(conversationId),
	}
}

const conversationIdReturnNote =
	'Tool conversation id; pass it back on subsequent search/execute calls.'

function conversationIdReturn(conversationId: string) {
	return `conversationId: ${conversationId}\n${conversationIdReturnNote}`
}

type TranscriptMemory = {
	id: string
	subject: string
	summary: string
}

function relevantMemoriesMarkdown(memories: Array<TranscriptMemory>) {
	return [
		'## Relevant memories',
		'',
		...memories.map((memory) => `- **${memory.subject}** — ${memory.summary}`),
	].join('\n')
}

export function searchTextReturn(input: {
	conversationId: string
	body: string
	memories?: Array<TranscriptMemory>
}) {
	const parts = [conversationIdReturn(input.conversationId), '', input.body]
	if (input.memories && input.memories.length > 0) {
		parts.push('', relevantMemoriesMarkdown(input.memories))
	}
	return parts.join('\n')
}

export function executeTextReturn(input: {
	conversationId: string
	value: unknown
	memories?: Array<TranscriptMemory>
}) {
	const parts = [
		conversationIdReturn(input.conversationId),
		'',
		jsonInput(input.value),
	]
	if (input.memories && input.memories.length > 0) {
		parts.push('', relevantMemoriesMarkdown(input.memories))
	}
	return parts.join('\n')
}
